/**
 * Patrón de Ruptura Quantum — constantes y resolución de config.
 *
 * SAFE_DEFAULTS es la única fuente de verdad de defaults; todo consumo de
 * config pasa por resolveConfig() (patrón E.2 del Standard) para que ninguna
 * clave llegue undefined aunque la config persistida sea parcial o corrupta.
 *
 * Los parámetros del motor (pivotLen … minChannelWidthAtr) tienen defaults
 * IDÉNTICOS al defaultConfig del motor (`./engine.js`, extraído byte-idéntico
 * del indicador legacy `breakout_pattern` retirado del catálogo el 2026-08-06): el rediseño solo cambia la presentación, no la
 * detección. Los colores se guardan como `null` (= usar paleta Quantum) y se
 * materializan con resolveColor al pintar o al mostrar el picker.
 */

export const QUANTUM_COLORS = Object.freeze({
  bull: '#1B5E20',
  bear: '#B71C1C',
  accent: '#B08D57',
  neutral: '#374151',
})

export const SAFE_DEFAULTS = Object.freeze({
  _version: 2,

  // ── Activación interna (sin toggle en el panel desde v1.2): false para
  // configs nuevas; el menú de indicadores pone enabled=true en la primera
  // activación. detect/render devuelven vacío con enabled === false. ────────
  enabled: false,

  // ── Visualización ────────────────────────────────────────────────────────
  showPatternLines: true,
  showSignalLabels: true,
  showLevels: true,
  showZones: true,
  showOutcomeMarkers: true,
  showChannelFill: true,
  showLastSignals: true,

  // ── Filtro opcional de activos favoritos: [] = TODOS los símbolos de la
  // plataforma (default); con valores, solo esos. Sin gating por timeframe:
  // el indicador corre siempre en el TF del chart. ──────────────────────────
  assets: [],

  // ── Budget (null = sin budget configurado; sin budget el dashboard muestra
  // el PnL del historial en R, con budget > 0 la estimación monetaria) ──────
  budget: null,

  // ── Notificaciones (el envío real requiere tarea backend; aquí solo se
  // persisten las preferencias) ────────────────────────────────────────────
  notificationsEnabled: false,
  notifyOnSignal: true,
  notifyOnTP1: true,
  notifyOnTP2: true,
  notifyOnTP3: true,
  notifyOnSL: true,

  // ── Estilo de línea ──────────────────────────────────────────────────────
  lineWidth: 1.5, // 1 | 1.5 | 2
  tp2LineStyle: 'dashed', // 'solid' | 'dashed'

  // ── Colores (null = paleta Quantum) ──────────────────────────────────────
  // resistance: bear · support: bull · sl: bear · tp: bull · entry: neutral
  // zone: color de las zonas riesgo/beneficio (null = bear/bull según lado)
  resistanceColor: null,
  supportColor: null,
  slColor: null,
  tpColor: null,
  entryColor: null,
  zoneColor: null,

  // ── Dashboard / panel ────────────────────────────────────────────────────
  badgePosition: { x: 8, y: 8 },
  panelSections: {},

  // ── Parámetros del motor (defaults = defaultConfig legacy; NO añadir,
  // renombrar ni reinterpretar) ────────────────────────────────────────────
  pivotLen: 5,
  minTouches: 2,
  maxChannelBars: 120,
  convergenceMin: 0.02,
  volConfirm: 1.2,
  volContraction: false,
  momentumFilter: true,
  slPadding: 0.5,
  touchTolerance: 0.15,
  deviationMax: 0.3,
  minChannelWidthAtr: 0.5,
})

// ── Rangos del panel (copiados del panel legacy breakout_pattern/Panel.jsx) ──
export const PIVOT_LEN_RANGE = Object.freeze({ min: 2, max: 30 })
export const MIN_TOUCHES_RANGE = Object.freeze({ min: 2, max: 6 })
export const MAX_CHANNEL_BARS_RANGE = Object.freeze({ min: 20, max: 500 })
export const CONVERGENCE_MIN_RANGE = Object.freeze({ min: 0, max: 0.5 })
export const VOL_CONFIRM_RANGE = Object.freeze({ min: 0.5, max: 3 })
export const SL_PADDING_RANGE = Object.freeze({ min: 0, max: 1 })
export const TOUCH_TOLERANCE_RANGE = Object.freeze({ min: 0.01, max: 0.5 })
export const DEVIATION_MAX_RANGE = Object.freeze({ min: 0.05, max: 1 })
export const MIN_CHANNEL_WIDTH_ATR_RANGE = Object.freeze({ min: 0.1, max: 3 })

export const LINE_WIDTHS = Object.freeze([1, 1.5, 2])
export const TP2_LINE_STYLES = Object.freeze(['solid', 'dashed'])

// Símbolos base soportados por la plataforma (crypto de SymbolSelector.jsx).
// Se ofrecen como favoritos opcionales; la lista vacía en config = todos.
export const AVAILABLE_SYMBOLS = Object.freeze(['BTC', 'ETH', 'SOL', 'XRP', 'DOGE', 'ADA', 'AVAX', 'LINK'])

// Opacidad fija de las zonas riesgo/beneficio (spec).
export const ZONE_OPACITY = 0.07

// Fuente serif de las etiquetas de señal (misma convención que la caja de
// posición de supertrend_quantum).
export const LABEL_FONT = "'Playfair Display', Georgia, serif"

// ── Helpers internos de saneado ─────────────────────────────────────────────

function clampNumber(v, min, max, fallback) {
  const n = typeof v === 'string' ? Number(v) : v
  if (!Number.isFinite(n)) return fallback
  return Math.min(max, Math.max(min, n))
}

function asBool(v, fallback) {
  return v == null ? fallback : Boolean(v)
}

function asEnum(v, allowed, fallback) {
  return allowed.includes(v) ? v : fallback
}

function asColorOrNull(v) {
  return typeof v === 'string' && v.length > 0 ? v : null
}

function asAssets(v) {
  if (!Array.isArray(v)) return [...SAFE_DEFAULTS.assets]
  // Filtra strings no válidos; el array vacío es VÁLIDO (= todos los
  // símbolos), no se cae a defaults.
  return v.filter(x => typeof x === 'string' && AVAILABLE_SYMBOLS.includes(x))
}

function asBudget(v) {
  const n = typeof v === 'string' ? Number(v) : v
  return Number.isFinite(n) && n > 0 ? n : null
}

/**
 * Normaliza un símbolo de chart ('BTC/USDT:USDT', 'BTC/USDT', 'BTCUSDT',
 * 'btc'…) a su base en mayúsculas ('BTC') para comparar con cfg.assets.
 */
export function normalizeSymbol(rawSymbol) {
  if (typeof rawSymbol !== 'string' || rawSymbol.length === 0) return null
  let s = rawSymbol.toUpperCase().trim()
  // Quita sufijos de par conocidos, del más específico al más general.
  for (const suffix of ['/USDT:USDT', '/USDT', 'USDT']) {
    if (s.endsWith(suffix)) {
      s = s.slice(0, -suffix.length)
      break
    }
  }
  return s || null
}

/**
 * ¿El símbolo crudo del chart pasa el filtro de activos? Lista vacía = todos
 * los símbolos (semántica v1.2: los activos son un filtro opcional de
 * favoritos). Con valores, compara la base normalizada.
 */
export function symbolMatches(assets, rawSymbol) {
  if (!Array.isArray(assets)) return false
  if (assets.length === 0) return true
  const base = normalizeSymbol(rawSymbol)
  if (!base) return false
  return assets.some(a => normalizeSymbol(a) === base)
}

/**
 * Config de fábrica para el botón "Restablecer valores Quantum": defaults de
 * la CONFIG (colores, grosores, toggles visuales, budget, parámetros motor…).
 * NUNCA toca: `enabled` (el reset no apaga ni enciende el indicador — bug
 * histórico: el reset lo eliminaba del chart al escribir enabled:false),
 * `badgePosition` (el badge no salta de sitio) ni `panelSections` (acordeones
 * del panel). La membresía del menú de indicadores vive fuera de esta config.
 */
export function resetToDefaults(current) {
  const pos = current?.badgePosition
  return {
    ...SAFE_DEFAULTS,
    enabled: current?.enabled ?? SAFE_DEFAULTS.enabled,
    badgePosition: pos && Number.isFinite(pos.x) ? pos : { x: 8, y: 8 },
    panelSections:
      current?.panelSections && typeof current.panelSections === 'object'
        ? current.panelSections
        : { ...SAFE_DEFAULTS.panelSections },
  }
}

/**
 * Merge defensivo: defaults + config persistida + saneado por clave.
 * Nunca devuelve undefined en ninguna clave conocida. Si `raw` no es un
 * objeto, devuelve una copia de SAFE_DEFAULTS.
 */
export function resolveConfig(raw) {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    return { ...SAFE_DEFAULTS }
  }
  const merged = { ...SAFE_DEFAULTS, ...raw }
  return {
    ...merged,
    enabled: asBool(merged.enabled, false),
    showPatternLines: asBool(merged.showPatternLines, true),
    showSignalLabels: asBool(merged.showSignalLabels, true),
    showLevels: asBool(merged.showLevels, true),
    showZones: asBool(merged.showZones, true),
    showOutcomeMarkers: asBool(merged.showOutcomeMarkers, true),
    showChannelFill: asBool(merged.showChannelFill, true),
    showLastSignals: asBool(merged.showLastSignals, true),
    assets: asAssets(merged.assets),
    budget: asBudget(merged.budget),
    notificationsEnabled: asBool(merged.notificationsEnabled, false),
    notifyOnSignal: asBool(merged.notifyOnSignal, true),
    notifyOnTP1: asBool(merged.notifyOnTP1, true),
    notifyOnTP2: asBool(merged.notifyOnTP2, true),
    notifyOnTP3: asBool(merged.notifyOnTP3, true),
    notifyOnSL: asBool(merged.notifyOnSL, true),
    lineWidth: LINE_WIDTHS.includes(merged.lineWidth) ? merged.lineWidth : SAFE_DEFAULTS.lineWidth,
    tp2LineStyle: asEnum(merged.tp2LineStyle, TP2_LINE_STYLES, SAFE_DEFAULTS.tp2LineStyle),
    resistanceColor: asColorOrNull(merged.resistanceColor),
    supportColor: asColorOrNull(merged.supportColor),
    slColor: asColorOrNull(merged.slColor),
    tpColor: asColorOrNull(merged.tpColor),
    entryColor: asColorOrNull(merged.entryColor),
    zoneColor: asColorOrNull(merged.zoneColor),
    badgePosition: merged.badgePosition && Number.isFinite(merged.badgePosition.x)
      ? merged.badgePosition
      : { x: 8, y: 8 },
    panelSections: merged.panelSections && typeof merged.panelSections === 'object' ? merged.panelSections : {},
    pivotLen: Math.round(clampNumber(merged.pivotLen, PIVOT_LEN_RANGE.min, PIVOT_LEN_RANGE.max, SAFE_DEFAULTS.pivotLen)),
    minTouches: Math.round(clampNumber(merged.minTouches, MIN_TOUCHES_RANGE.min, MIN_TOUCHES_RANGE.max, SAFE_DEFAULTS.minTouches)),
    maxChannelBars: Math.round(clampNumber(merged.maxChannelBars, MAX_CHANNEL_BARS_RANGE.min, MAX_CHANNEL_BARS_RANGE.max, SAFE_DEFAULTS.maxChannelBars)),
    convergenceMin: clampNumber(merged.convergenceMin, CONVERGENCE_MIN_RANGE.min, CONVERGENCE_MIN_RANGE.max, SAFE_DEFAULTS.convergenceMin),
    volConfirm: clampNumber(merged.volConfirm, VOL_CONFIRM_RANGE.min, VOL_CONFIRM_RANGE.max, SAFE_DEFAULTS.volConfirm),
    volContraction: asBool(merged.volContraction, false),
    momentumFilter: asBool(merged.momentumFilter, true),
    slPadding: clampNumber(merged.slPadding, SL_PADDING_RANGE.min, SL_PADDING_RANGE.max, SAFE_DEFAULTS.slPadding),
    touchTolerance: clampNumber(merged.touchTolerance, TOUCH_TOLERANCE_RANGE.min, TOUCH_TOLERANCE_RANGE.max, SAFE_DEFAULTS.touchTolerance),
    deviationMax: clampNumber(merged.deviationMax, DEVIATION_MAX_RANGE.min, DEVIATION_MAX_RANGE.max, SAFE_DEFAULTS.deviationMax),
    minChannelWidthAtr: clampNumber(merged.minChannelWidthAtr, MIN_CHANNEL_WIDTH_ATR_RANGE.min, MIN_CHANNEL_WIDTH_ATR_RANGE.max, SAFE_DEFAULTS.minChannelWidthAtr),
  }
}

/** null → color de paleta; string no vacío → tal cual. */
export function resolveColor(value, fallback) {
  return typeof value === 'string' && value.length > 0 ? value : fallback
}

/** Materializa los colores del indicador a partir de una config resuelta. */
export function resolveColors(cfg) {
  return {
    resistance: resolveColor(cfg.resistanceColor, QUANTUM_COLORS.bear),
    support: resolveColor(cfg.supportColor, QUANTUM_COLORS.bull),
    sl: resolveColor(cfg.slColor, QUANTUM_COLORS.bear),
    tp: resolveColor(cfg.tpColor, QUANTUM_COLORS.bull),
    entry: resolveColor(cfg.entryColor, QUANTUM_COLORS.neutral),
  }
}

/** hex '#RRGGBB' o 'rgb(...)' → 'rgba(r, g, b, alpha)'. */
export function withAlpha(color, alpha) {
  const a = Math.min(1, Math.max(0, alpha))
  if (typeof color !== 'string' || color.length === 0) return `rgba(0,0,0,${a})`
  if (color.startsWith('rgba(')) {
    return color.replace(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*[\d.]+\)/, `rgba($1, $2, $3, ${a})`)
  }
  if (color.startsWith('rgb(')) {
    const rgb = color.match(/\d+/g)
    return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${a})`
  }
  if (color.startsWith('#')) {
    const hex = color.slice(1)
    const n = parseInt(hex, 16)
    if (Number.isNaN(n) || hex.length < 6) return color
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`
  }
  return color
}
